import { zodResolver } from '@hookform/resolvers/zod';
import { CalendarIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import BreadCrumb from '../../breadcrumb';
import Header from '../../DashboardNavigation/header';
import Sidebar from '../../DashboardNavigation/sidebar';
import { Button } from '../../ui/button';
import { Checkbox } from '../../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../ui/form';
import { Heading } from '../../ui/heading';
import { Input } from '../../ui/input';
import { Separator } from '../../ui/separator';
import {
  NewsFormValues,
  newsSchema,
  updatedNewsSchema,
  UpdatedNewsSchema,
} from './news-zod.schema';

import { getNewsById, handleCreateNews, handleUpdateNews } from '../../../API/News/newsService';
import QuillEditor from '../../../lib/quill/quill';
import { cn } from '../../../lib/utils';
import FileUpload from '../../FileUpload/FileUpload';
import { Calendar } from '../../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';

export const NewsForm: React.ComponentType<any> = () => {
  const uploadUrl = process.env.REACT_APP_FILE_UPLOAD;

  const router = useNavigate();
  const { id } = useParams();
  const initialData = id ? parseInt(id) : undefined;

  const [loading, setLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const title = initialData ? 'Modifier actualité' : 'Ajouter de nouvelles actualités';
  const description = initialData ? 'Modifier actualité' : '';

  const form = useForm<NewsFormValues | UpdatedNewsSchema>({
    resolver: zodResolver(initialData ? updatedNewsSchema : newsSchema),
    defaultValues: {
      title: '',
      date: undefined,
      description: '',
      image: [],
      isDraft: false,
    },
    mode: 'onChange',
  });

  const isDraftValue = form.watch('isDraft');

  const onSubmit = async (values: NewsFormValues | UpdatedNewsSchema) => {
    setLoading(true);
    try {
      if (initialData) {
        await handleUpdateNews(initialData, values, router);
      } else {
        await handleCreateNews(values, router);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const [data, setData] = useState<any>();
  useEffect(() => {
    const fetchData = async () => {
      if (initialData) {
        const newsData = await getNewsById(initialData);
        setData(newsData);
        form.reset({
          title: newsData.title,
          description: newsData.description,
          date: newsData.date ? new Date(newsData.date) : undefined,
          isDraft: newsData.isDraft ?? false,
          image: newsData.image
            ? [
                {
                  fileName: newsData.image,
                  name: newsData.image,
                  fileSize: 0,
                  size: 0,
                  key: newsData.image,
                },
              ]
            : [],
        });
      }
    };

    if (initialData) {
      fetchData();
    }
  }, [initialData]);

  const breadcrumbItems = [
    { title: 'Actualités', link: '/admin-dashboard/actualités' },
    {
      title: initialData ? 'Mise à jour' : 'Ajouter',
      link: '/admin-dashboard/actualité/ajouter',
    },
  ];

  // Données pour l'aperçu
  const previewValues = form.watch();
  const previewImageUrl =
    previewValues.image && previewValues.image.length > 0
      ? previewValues.image[0].file
        ? URL.createObjectURL(previewValues.image[0].file)
        : data?.image
          ? `${uploadUrl}/news/${data.image}`
          : null
      : data?.image
        ? `${uploadUrl}/news/${data.image}`
        : null;

  return (
    <>
      <Header />
      <div className="flex h-screen">
        <Sidebar />
        <div className="w-full p-10 overflow-y-auto">
          <BreadCrumb items={breadcrumbItems} />
          <div className="flex items-center justify-between">
            <Heading title={title} description={description} />
          </div>
          <Separator />
          <Form {...form}>
            <form
              className="w-full space-y-8"
              onSubmit={form.handleSubmit(onSubmit)}
              encType="multipart/form-data"
            >
              <div className="flex justify-between">
                <div className="w-1/2">
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Titre</FormLabel>
                        <FormControl>
                          <Input disabled={loading} placeholder={'Titre...'} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div>
                  <FormField
                    control={form.control}
                    name="date"
                    render={({ field }) => (
                      <FormItem className="flex flex-col mt-3">
                        <FormLabel>Date de publication</FormLabel>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant={'outline'}
                              className={cn(
                                'w-[280px] justify-start text-left font-normal',
                                !field.value && 'text-muted-foreground'
                              )}
                            >
                              <CalendarIcon className="w-4 h-4 mr-2" />
                              {field.value ? (
                                field.value.toLocaleDateString('fr-FR')
                              ) : (
                                <span>Date début</span>
                              )}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0">
                            <Calendar
                              mode="single"
                              selected={field.value}
                              onSelect={date => {
                                field.onChange(date);
                              }}
                            />
                          </PopoverContent>
                        </Popover>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="w-full">
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <QuillEditor
                          value={field.value || ''}
                          onChange={(value: string) => {
                            field.onChange(value);
                            form.trigger('description');
                          }}
                          path="news"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Checkbox mode brouillon */}
                <FormField
                  control={form.control}
                  name="isDraft"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center space-x-3 space-y-0 mt-4">
                      <FormControl>
                        <Checkbox
                          checked={field.value ?? false}
                          onCheckedChange={checked => {
                            field.onChange(checked);
                            // Re-valider l'image lors du changement
                            form.trigger('image');
                          }}
                        />
                      </FormControl>
                      <FormLabel className="cursor-pointer font-medium">
                        Sauvegarder en brouillon
                      </FormLabel>
                    </FormItem>
                  )}
                />

                {/* Image existante */}
                {data?.image && (
                  <div className="mt-4 mb-2">
                    <p className="text-sm text-muted-foreground mb-1">Image actuelle :</p>
                    <img
                      src={`${uploadUrl}/news/${data.image}`}
                      alt="Image actuelle"
                      className="w-24 h-24 object-cover rounded"
                    />
                  </div>
                )}

                {/* Upload image — non requis si brouillon */}
                <FormField
                  control={form.control}
                  name="image"
                  render={() => (
                    <FormItem className="mt-4">
                      <FormLabel>
                        Image{' '}
                        {isDraftValue ? (
                          <span className="text-muted-foreground text-xs">
                            (optionnelle en mode brouillon)
                          </span>
                        ) : (
                          <span className="text-red-500">*</span>
                        )}
                      </FormLabel>
                      <FormControl>
                        <FileUpload
                          onChange={files => {
                            const mappedFiles = files.map(file => ({
                              key: file.id,
                              fileName: file.fileName,
                              name: file.fileName,
                              fileSize: parseInt(file.fileSize),
                              size: parseInt(file.fileSize),
                              file: file.file,
                            }));
                            form.setValue('image', mappedFiles);
                            form.trigger('image');
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  onClick={() => router(-1)}
                  disabled={loading}
                  className="ml-auto bg-red-500 me-2"
                  type="button"
                >
                  Annuler
                </Button>
                <Button
                  disabled={loading}
                  isLoading={loading}
                  className="ml-auto me-2"
                  type="submit"
                >
                  {initialData ? 'Modifier' : 'Ajouter'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={loading}
                  onClick={() => setPreviewOpen(true)}
                >
                  Aperçu avant publication
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </div>

      {/* Modal aperçu avant publication */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Aperçu avant publication</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {previewValues.isDraft && (
              <div className="bg-yellow-100 border border-yellow-400 text-yellow-800 text-sm px-3 py-2 rounded">
                Mode brouillon — cette actualité ne sera pas visible publiquement.
              </div>
            )}
            <h2 className="text-2xl font-bold">
              {previewValues.title || (
                <span className="text-muted-foreground italic">Titre non renseigné</span>
              )}
            </h2>
            <p className="text-sm text-muted-foreground">
              {previewValues.date ? (
                previewValues.date.toLocaleDateString('fr-FR')
              ) : (
                <span className="italic">Date non renseignée</span>
              )}
            </p>
            {previewImageUrl && (
              <img
                src={previewImageUrl}
                alt="Aperçu"
                className="w-full max-h-64 object-cover rounded"
              />
            )}
            {previewValues.description ? (
              <div
                className="prose max-w-none"
                dangerouslySetInnerHTML={{ __html: previewValues.description }}
              />
            ) : (
              <p className="text-muted-foreground italic">Description non renseignée</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
